import { log } from "next-axiom";

import { create } from "zustand";

import { createApolloClient } from "../../../../lib/apollo";
import generateIterableJwtQuery from "@/Apollo/mutation/generateIterableJwt.graphql";
import { isBrowser } from "@/common/utils/general.utils";
import { ITERABLE_AUTH_TOKEN } from "@/config";
import {
  DisplayOptions,
  HandleLinks,
  IterableConfig,
  IterableEmbeddedManager,
  IterableEmbeddedMessage,
  IterableEmbeddedMessageUpdateHandler,
  IterableEmbeddedSessionManager,
  WithJWT,
  getInAppMessages,
  initializeWithConfig,
  track,
} from "@iterable/web-sdk";

export const ITERABLE_PACKAGE_NAME = "whoppah";

let iterableUserId: string | undefined;
let iterableEmbeddedManager: IterableEmbeddedManager | undefined;
let iterableEmbeddedSessionManager: IterableEmbeddedSessionManager | undefined;
let iterableClient: WithJWT | undefined;

/**
 * Tracks if SDK has attached a user to requests for the current session.
 * Reset on page load and logout. Prevents INITIALIZE_ERROR on early requests.
 */
let iterableAuthReady = false;

const PLACEMENT_HOME = 95;
const PLACEMENT_HOME_TOP_BRAND = 354;
const PLACEMENT_HOME_BOTTOM_BURST = 355;

const PLACEMENT_PDP = 97;

/**
 * Override Iterable's default behavior to open URLs in the same window.
 * Returning true prevents the SDK from opening a new tab.
 */
IterableConfig.urlHandler = {
  handleIterableURL: uri => {
    window.location.assign(uri);
    return true;
  },
};

const initIterable = () => {
  if (!isBrowser()) {
    return;
  }

  if (iterableClient) {
    return;
  }

  iterableEmbeddedManager = new IterableEmbeddedManager(ITERABLE_PACKAGE_NAME);

  iterableEmbeddedSessionManager = new IterableEmbeddedSessionManager(
    ITERABLE_PACKAGE_NAME
  );

  iterableClient = initializeWithConfig({
    authToken: ITERABLE_AUTH_TOKEN || "",
    configOptions: {
      enableUnknownActivation: true,
      isEuIterableService: true,
      onUnknownUserCreated: async userId => {
        console.log("onUnknownUserCreated fired", userId);
        log.info("Anonymous user created in Iterable", { userId });
        /**
         * Wait for SDK to attach user to requests before fetching.
         * Prevents INITIALIZE_ERROR on early calls.
         */
        await waitForAuthReady();
        await loadMessages();
      },
      identityResolution: {
        replayOnVisitorToKnown: true,
        mergeOnUnknownToKnown: true,
      },
    },
    generateJWT: async ({ userID }) => {
      const client = createApolloClient();
      const { data } = await client.mutate({
        mutation: generateIterableJwtQuery,
        variables: { input: { userId: userID } },
      });
      /**
       * The SDK attaches the user to outgoing requests (sets `typeOfAuth`)
       * synchronously right after this resolves. Flag readiness on a macrotask
       * so that has definitely happened before any waiter proceeds.
       */
      setTimeout(() => {
        iterableAuthReady = true;
      }, 0);
      return data?.generateIterableJwt.token || "";
    },
  });

  /**
   * Set the visitor usage tracked to true will send a usage event to Iterable.
   * This also restores an already-activated anonymous user from localStorage.
   */
  iterableClient.setVisitorUsageTracked(true);

  /**
   * On reload, the SDK restores an already-activated anonymous user but does
   * NOT re-fire `onUnknownUserCreated`, and `setUserId` is never called for a
   * logged-out visitor — so nothing would fetch their messages. Trigger it
   * explicitly once the SDK has re-attached the restored user to requests.
   */
  const existingUnknownUserId = localStorage.getItem(
    ITERABLE_UNKNOWN_USER_ID_KEY
  );

  if (existingUnknownUserId) {
    waitForAuthReady().then(() => loadMessages());
  }

  bindSessionToVisibility();
};

const handleClick = (url: string) => {
  if (!iterableEmbeddedManager) {
    return;
  }

  iterableEmbeddedManager.click(url);
};

const startSession = () => {
  if (!iterableEmbeddedSessionManager) {
    return;
  }

  iterableEmbeddedSessionManager.startSession();
};

const endSession = () => {
  if (!iterableEmbeddedSessionManager) {
    return;
  }

  iterableEmbeddedSessionManager.endSession();
};

/**
 * Ending the embedded session is what flushes the `embeddedSession` event (with
 * its batched impressions) to Iterable, so tie it to tab visibility: React
 * unmount never runs when the user just closes or backgrounds the tab.
 */
const bindSessionToVisibility = () => {
  startSession();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      endSession();
    } else {
      startSession();
    }
  });
};

/**
 * Impressions are batched by the session manager and sent with the
 * `embeddedSession` event when the session ends.
 *
 * ponytail: "displayed" means rendered, not scrolled into view. Swap in an
 * IntersectionObserver if the durations ever need to be viewport-accurate.
 */
const startImpression = (messageId: string, placementId?: number) => {
  if (placementId === undefined) {
    return;
  }

  iterableEmbeddedSessionManager?.startImpression(messageId, placementId);
};

const pauseImpression = (messageId: string) => {
  iterableEmbeddedSessionManager?.pauseImpression(messageId);
};

type DismissedEvent = {
  messageId: string;
  campaignId?: number;
  embeddedKey?: string;
  placement: "top" | "bottom";
};

/**
 * Custom event CRM segments on to keep a dismissed message suppressed across
 * devices. Iterable's own `embeddedDismiss` event can't be used for that — its
 * button identifier is empty, so it isn't filterable.
 *
 * Payload is kept identical to the one the apps send (which Iterable accepts),
 * including `dismissMethod`, so one segment covers web and app. In particular
 * there is no top-level `campaignId`: Iterable validates that against the
 * campaign the event is attributed to, and a rejected event is dropped whole.
 * The id CRM needs is in `dataFields`.
 */
const trackDismissed = ({
  messageId,
  campaignId,
  embeddedKey,
  placement,
}: DismissedEvent) => {
  track({
    eventName: "embedded_dismissed",
    dataFields: {
      messageId,
      campaignId,
      embeddedKey,
      placement,
      platform: "web",
      dismissMethod: "x_button",
    },
  }).catch(ex => {
    /**
     * The event is invisible once it leaves the page, so failures go to Axiom
     * rather than the visitor's console — that is the only way we find out the
     * suppression segment is missing events.
     */
    log.warn("embedded_dismissed failed", {
      messageId,
      campaignId,
      reason: ex instanceof Error ? ex.message : String(ex),
    });
  });
};

const logout = () => {
  iterableClient?.logout();
  /**
   * The SDK clears `typeOfAuth` on logout, so requests need a freshly attached
   * user again before they'll succeed.
   */
  iterableAuthReady = false;
};

type EmbeddedMessage = IterableEmbeddedMessage & {
  type: "homepage" | "homepage_top_brand" | "homepage_bottom_burst" | "pdp";
};

type IterableStore = {
  home: EmbeddedMessage[];
  pdp: EmbeddedMessage[];
  setUserId: (id: string) => void;
  logout: () => void;
};

/**
 * localStorage key for SDK's anonymous user id. Presence on init means
 * restoring an anonymous user from a previous session.
 */
const ITERABLE_UNKNOWN_USER_ID_KEY = "unknown_userId";

/**
 * Wait until the SDK attaches a user to outgoing requests. Polls briefly and
 * resolves either way. Prevents INITIALIZE_ERROR rejections on early fetches.
 */
const waitForAuthReady = async (attempts = 30) => {
  for (let i = 0; i < attempts; i++) {
    if (iterableAuthReady) {
      return;
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
};

/**
 * Fetch and sync messages for the active user (known or anonymous).
 */
const loadMessages = async () => {
  if (!iterableEmbeddedManager) {
    return;
  }

  /**
   * Embedded messages — the homepage/PDP tiles the app renders. Set this up
   * FIRST and independently of the in-app message request below (a separate
   * feature), so a failing or hanging in-app request can never block it.
   */
  const updateListener: IterableEmbeddedMessageUpdateHandler = {
    onMessagesUpdated(): void {
      if (!iterableEmbeddedManager) {
        return;
      }

      const home =
        iterableEmbeddedManager.getMessagesForPlacement(PLACEMENT_HOME);

      const homeTopBrand = iterableEmbeddedManager.getMessagesForPlacement(
        PLACEMENT_HOME_TOP_BRAND
      );

      const homeBottomBurst = iterableEmbeddedManager.getMessagesForPlacement(
        PLACEMENT_HOME_BOTTOM_BURST
      );

      const pdp =
        iterableEmbeddedManager.getMessagesForPlacement(PLACEMENT_PDP);

      iterableStore.setState({
        home: [
          ...home.map<EmbeddedMessage>(message => ({
            ...message,
            type: "homepage",
          })),
          ...homeTopBrand.map<EmbeddedMessage>(message => ({
            ...message,
            type: "homepage_top_brand",
          })),
          ...homeBottomBurst.map<EmbeddedMessage>(message => ({
            ...message,
            type: "homepage_bottom_burst",
          })),
        ],
        pdp: pdp.map<EmbeddedMessage>(message => ({
          ...message,
          type: "pdp",
        })),
      });
    },
    onEmbeddedMessagingDisabled(): void {
      iterableStore.setState({
        home: [],
        pdp: [],
      });
    },
  };

  iterableEmbeddedManager.addUpdateListener(updateListener);
  iterableEmbeddedManager.syncMessages(ITERABLE_PACKAGE_NAME, () => {});

  /**
   * In-app messages (popups) — a separate feature from embedded tiles. Fire it
   * in isolation so a failure or hang here can't affect embedded messages.
   */
  try {
    const { request } = getInAppMessages(
      {
        /**
         * Here, configure the SDK. For more information, check out the web
         * SDK's GitHub repository: https://github.com/iterable/iterable-web-sdk
         */
        count: 20,
        displayInterval: 1000,
        onOpenScreenReaderMessage: "...",
        onOpenNodeToTakeFocus: "input",
        packageName: ITERABLE_PACKAGE_NAME,
        rightOffset: "20px",
        topOffset: "20px",
        bottomOffset: "20px",
        handleLinks: HandleLinks.ExternalNewTab,
        closeButton: {
          color: "black",
          size: "16px",
          topOffset: "20px",
        },
      },
      { display: DisplayOptions.Immediate }
    );

    await request();
  } catch (ex) {
    console.error("in-app messages request failed", ex);
  }
};

export const iterable = {
  init: initIterable,
  click: handleClick,
  startImpression,
  pauseImpression,
  dismissed: trackDismissed,
};

export const iterableStore = create<IterableStore>(() => ({
  home: [],
  pdp: [],
  setUserId: async (id: string) => {
    /**
     * We don't want to trigger identify again if the user id is the same.
     */
    if (iterableUserId === id) {
      return;
    }

    /**
     * Keep track of the last user id.
     */
    iterableUserId = id;

    try {
      /**
       * Promote the visitor to a known user. Anonymous visitors are handled by
       * Unknown User Activation and never reach this path.
       */
      await iterableClient?.setUserID(id);
    } catch (ex) {
      console.error(ex);
      return;
    }

    await loadMessages();
  },
  logout,
}));
